import React, { useMemo, useState } from 'react';

import {
    DailyTasksShiftDetail,
    ResidentTasks,
    TaskToUpdate,
} from 'types/dailyTasks';

import { useDailyTasks } from 'api/queries/tasks/dailyTasks';
import EditTaskStatusDialog from 'components/Shared/EditTaskStatusDialog';
import { AddTaskNotesDialog } from 'components/Shared/Task/AddTaskNotesDialog';
import { TaskNotesViewDialog } from 'components/Shared/Task/TaskNotesViewDialog';
import { useAppDispatch } from 'constants/redux';
import { DIALOG_TYPE } from 'constants/shared';
import { updateDailyTasks } from 'redux/actions/dailyTasks';

import { MultiSelectProvider } from './MultiSelectProvider';
import { ResidentRow } from './ResidentRow';

type Props = {
    branchId: number;
    date: string;
    residentTasksList: ResidentTasks[];
};

export const ResidentRows = (props: Props) => {
    const { residentTasksList, branchId, date } = props;

    const [viewTaskIdNotes, setViewTaskIdNotes] = useState<number | null>(null);

    const [showTaskIdAddNotesDialog, setShowTaskIdAddNotesDialog] = useState<
        number | null
    >(null);

    const [showTaskIdRejectDialog, setShowTaskIdRejectDialog] = useState<
        number | null
    >(null);

    const [taskIdCompletionOptions, setTaskIdCompletionOptions] = useState<{
        [key: string]: number;
    }>({});

    const dispatch = useAppDispatch();

    const { refetch: dailyTasksRefetch } = useDailyTasks({
        branchId: Number(branchId),
        date,
    });

    const taskIdToTask: Record<number, DailyTasksShiftDetail> = useMemo(
        () =>
            residentTasksList.reduce((acc, { tasks }) => {
                tasks.forEach((task) => {
                    acc[task.dailyTaskRecordId] = task;
                });

                return acc;
            }, {}),
        [residentTasksList]
    );

    const onToggleTaskNotes = (taskId = null) => {
        setViewTaskIdNotes(taskId);
    };

    const onToggleEditAddTaskNotes = (taskId = null) => {
        setShowTaskIdAddNotesDialog(taskId);
    };

    const onSubmitEditAddTaskNotes = (taskNotes) => {
        // submit note and refresh tasks
        onUpdateConfirmedTask(showTaskIdAddNotesDialog, { taskNotes });
        setShowTaskIdAddNotesDialog(null);
    };

    const onToggleRejectDialog = (taskId = null) => {
        setShowTaskIdRejectDialog(taskId);
    };

    const onRejectDialogSubmit = (taskStatusId, notes) => {
        onUpdateConfirmedTask(showTaskIdRejectDialog, {
            taskStatusId,
            taskNotes: notes,
        });
        setShowTaskIdRejectDialog(null);
    };

    const onCompletionOptionsChange = (taskId, completionValue) => {
        setTaskIdCompletionOptions({
            ...taskIdCompletionOptions,
            [taskId]: completionValue,
        });
    };

    const onUpdateConfirmedTask = async (
        taskId,
        { taskStatusId = null, taskNotes = null }
    ) => {
        const originalTask = taskIdToTask[taskId];
        const updatedTaskStatusId = taskStatusId || originalTask.taskStatusId;
        const updatedTaskNotes = taskNotes || originalTask.caregiverNotes;

        const tasksToUpdate: TaskToUpdate[] = [];

        tasksToUpdate.push({
            taskId,
            taskStatusId: updatedTaskStatusId,
            caregiverNotes: updatedTaskNotes,
        });

        await dispatch(updateDailyTasks({ tasks: tasksToUpdate }));

        dailyTasksRefetch();
    };

    if (residentTasksList.length === 0) {
        return null;
    }

    return (
        <MultiSelectProvider
            render={(openedResidentId, onResidentToggle) => (
                <>
                    {residentTasksList.map((residentTasks) => (
                        <ResidentRow
                            key={residentTasks.id}
                            isOpen={openedResidentId === residentTasks.id}
                            residentTasks={residentTasks}
                            onResidentToggle={onResidentToggle}
                            onToggleTaskNotes={onToggleTaskNotes}
                            onToggleEditAddTaskNotes={onToggleEditAddTaskNotes}
                            onToggleRejectDialog={onToggleRejectDialog}
                            onCompletionOptionsChange={
                                onCompletionOptionsChange
                            }
                            taskIdCompletionOptions={taskIdCompletionOptions}
                        />
                    ))}
                    <TaskNotesViewDialog
                        isOpen={viewTaskIdNotes !== null}
                        taskNote={
                            viewTaskIdNotes
                                ? taskIdToTask[viewTaskIdNotes].taskNotes
                                : ''
                        }
                        onClose={() => onToggleTaskNotes()}
                    />
                    <AddTaskNotesDialog
                        isOpen={showTaskIdAddNotesDialog !== null}
                        onClose={() => onToggleEditAddTaskNotes()}
                        onSubmit={onSubmitEditAddTaskNotes}
                        taskNote={
                            showTaskIdAddNotesDialog
                                ? taskIdToTask[showTaskIdAddNotesDialog]
                                      .caregiverNotes
                                : ''
                        }
                    />
                    <EditTaskStatusDialog
                        isOpen={showTaskIdRejectDialog !== null}
                        onClose={onToggleRejectDialog}
                        onSubmit={onRejectDialogSubmit}
                        selectedRecord={
                            showTaskIdRejectDialog
                                ? taskIdToTask[showTaskIdRejectDialog]
                                : undefined
                        }
                        dialogType={DIALOG_TYPE.edit}
                    />
                </>
            )}
        />
    );
};
